import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { sendInquiryEmail } from '@/lib/web3forms';

let modalOpenFn: ((productName?: string) => void) | null = null;

export function openInquiryModal(productName?: string) {
  modalOpenFn?.(productName);
}

export default function InquiryModal() {
  const [open, setOpen] = useState(false);
  const [productName, setProductName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    modalOpenFn = (name?: string) => {
      setProductName(name || '');
      setSubmitted(false);
      setError('');
      setOpen(true);
      document.body.style.overflow = 'hidden';
    };
    return () => {
      modalOpenFn = null;
    };
  }, []);

  const close = () => {
    setOpen(false);
    document.body.style.overflow = '';
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const hp = formData.get('company_alt') as string;
    if (hp && hp.trim() !== '') {
      setSubmitted(true);
      return;
    }

    setSending(true);
    setError('');

    const name = (formData.get('name') as string) || '';
    const email = (formData.get('email') as string) || '';
    const company = (formData.get('company') as string) || '';
    const phone = (formData.get('phone') as string) || '';
    const interest = (formData.get('interest') as string) || '';
    const message = (formData.get('message') as string) || '';

    try {
      const { error: supabaseErr } = await supabase.from('inquiries').insert({
        name,
        email,
        company: company || null,
        phone: phone || null,
        product_interest: interest || null,
        message: message || null,
        status: 'new',
      });
      if (supabaseErr) console.error('Supabase insert error:', supabaseErr);
    } catch (err) {
      console.error('Supabase insert failed:', err);
    }

    const result = await sendInquiryEmail({ name, email, company, phone, interest, message });

    if (result.ok) {
      setSubmitted(true);
      setError('');
      form.reset();
    } else {
      setError(result.message);
    }

    setSending(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={close}
      ></div>

      {/* Modal */}
      <div className="relative z-10 w-full max-w-lg max-h-[90vh] overflow-y-auto bg-background-50 rounded-2xl border border-background-200/70 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-background-100">
          <div>
            <h2 className="text-lg font-heading font-semibold text-foreground-950">Send Us a Message</h2>
            <p className="text-xs text-foreground-500 mt-0.5">We&apos;ll get back to you within 24 hours</p>
          </div>
          <button
            onClick={close}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors duration-200 text-foreground-400 hover:text-foreground-700 cursor-pointer"
            aria-label="Close"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                <i className="ri-check-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-heading font-semibold text-foreground-900 mb-2">Thank You!</h3>
              <p className="text-sm text-foreground-500 leading-relaxed max-w-xs mx-auto">
                Your inquiry has been received. Our team will get back to you within 24 hours.
              </p>
              <button
                onClick={close}
                className="mt-6 px-6 py-2.5 bg-accent-500 text-primary-900 font-semibold text-sm rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-readdy-form="true" className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-accent-400 transition-colors duration-200 text-foreground-800 placeholder:text-foreground-300"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">Company *</label>
                  <input
                    type="text"
                    name="company"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-accent-400 transition-colors duration-200 text-foreground-800 placeholder:text-foreground-300"
                    placeholder="Your Company Ltd"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">Email *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-accent-400 transition-colors duration-200 text-foreground-800 placeholder:text-foreground-300"
                    placeholder="you@company.com"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-accent-400 transition-colors duration-200 text-foreground-800 placeholder:text-foreground-300"
                    placeholder="+254 700 000 000"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">Product / Service Interest</label>
                <select
                  name="interest"
                  defaultValue={productName}
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-accent-400 transition-colors duration-200 text-foreground-800 cursor-pointer"
                >
                  <option value="">Select an option</option>
                  <option value="trading">Trading</option>
                  <option value="procurement">Procurement</option>
                  <option value="supply-chain">Supply Chain Management</option>
                  <option value="freight">Freight Forwarding</option>
                  <option value="clearing">Clearing & Forwarding</option>
                  {productName && <option value={productName}>{productName}</option>}
                  <option value="other">Other / Multiple</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">Message *</label>
                <textarea
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-accent-400 transition-colors duration-200 resize-none text-foreground-800 placeholder:text-foreground-300"
                  placeholder="Tell us about your requirements..."
                ></textarea>
              </div>

              <div className="hp-field">
                <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>

              {error && (
                <div className="text-xs text-red-500 bg-red-50 px-4 py-2.5 rounded-lg">{error}</div>
              )}

              <button
                type="submit"
                disabled={sending}
                className="w-full px-6 py-3.5 bg-accent-500 text-primary-900 font-semibold text-sm rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {sending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-primary-900/30 border-t-primary-900 rounded-full animate-spin"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    Send Inquiry
                    <i className="ri-send-plane-line"></i>
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}